/**
 * FacultyProfilePage - Manage faculty profile details
 */

import { useState, useEffect, useRef } from 'react';
import FacultyLayout from './FacultyLayout';
import {
  getFacultyProfile,
  updateFacultyProfile,
  uploadHeadshot,
  getStripeConnectStatus,
  getStripeDashboardUrl,
  getStripeOnboardingUrl,
  disconnectStripeAccount,
  formatFacultyStatus,
  getFacultyStatusColor,
  type FacultyProfile,
  type StripeConnectStatus,
} from '../../api/faculty';

export default function FacultyProfilePage() {
  const [profile, setProfile] = useState<FacultyProfile | null>(null);
  const [stripeStatus, setStripeStatus] = useState<StripeConnectStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const [profileData, stripeData] = await Promise.all([
        getFacultyProfile(),
        getStripeConnectStatus().catch(() => null),
      ]);
      setProfile(profileData);
      setStripeStatus(stripeData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load profile');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <FacultyLayout>
        <div className="max-w-2xl mx-auto">
          <div className="card animate-pulse">
            <div className="flex items-center gap-6 mb-6">
              <div className="w-24 h-24 bg-warm-100 rounded-full" />
              <div className="flex-1 space-y-2">
                <div className="h-6 bg-warm-100 rounded w-1/2" />
                <div className="h-4 bg-warm-100 rounded w-1/3" />
              </div>
            </div>
          </div>
        </div>
      </FacultyLayout>
    );
  }

  if (error || !profile) {
    return (
      <FacultyLayout>
        <div className="max-w-2xl mx-auto">
          <div className="card text-center py-12">
            <p className="text-danger-600 mb-4">{error}</p>
            <button onClick={loadData} className="btn-primary">
              Try Again
            </button>
          </div>
        </div>
      </FacultyLayout>
    );
  }

  return (
    <FacultyLayout>
      <div className="max-w-2xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-display font-bold text-warm-900 mb-2">
              My Profile
            </h1>
            <p className="text-warm-600">
              Manage your profile information and settings.
            </p>
          </div>
          {!isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className="btn-primary"
            >
              Edit Profile
            </button>
          )}
        </div>

        {isEditing ? (
          <ProfileEditor
            profile={profile}
            onSave={() => {
              setIsEditing(false);
              loadData();
            }}
            onCancel={() => setIsEditing(false)}
          />
        ) : (
          <>
            {/* Profile Card */}
            <div className="card mb-6">
              <div className="flex flex-col items-center sm:flex-row sm:items-start gap-4 sm:gap-6">
                {/* Headshot */}
                <div className="w-24 h-24 rounded-full bg-warm-100 overflow-hidden flex-shrink-0">
                  {profile.headshot_url ? (
                    <img
                      src={profile.headshot_url}
                      alt={`${profile.first_name} ${profile.last_name}`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <svg className="w-10 h-10 text-warm-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h2 className="text-xl font-semibold text-warm-900">
                      {profile.first_name} {profile.last_name}
                    </h2>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getFacultyStatusColor(profile.status)}`}>
                      {formatFacultyStatus(profile.status)}
                    </span>
                  </div>
                  <p className="text-warm-600">{profile.email}</p>
                  {profile.phone && (
                    <p className="text-warm-600">{profile.phone}</p>
                  )}
                </div>
              </div>

              {/* Bio */}
              {profile.bio && (
                <div className="mt-6 pt-6 border-t border-warm-200">
                  <h3 className="text-sm font-medium text-warm-500 uppercase tracking-wide mb-2">
                    Bio
                  </h3>
                  <p className="text-warm-700 whitespace-pre-wrap">{profile.bio}</p>
                </div>
              )}
            </div>

            {/* Certified Shows */}
            {profile.shows && profile.shows.length > 0 && (
              <div className="card mb-6">
                <h3 className="text-sm font-medium text-warm-500 uppercase tracking-wide mb-4">
                  Certified Classes
                </h3>
                <div className="space-y-2">
                  {profile.shows.map((show) => (
                    <div key={show.id} className="flex items-center gap-3 p-3 bg-warm-50 rounded-lg">
                      <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                        {show.logo_url ? (
                          <img src={show.logo_url} alt={show.name} className="w-full h-full object-cover rounded-lg" />
                        ) : (
                          <span className="text-primary-600 font-bold">{show.name.charAt(0)}</span>
                        )}
                      </div>
                      <span className="font-medium text-warm-900">{show.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Payment Settings */}
            <div className="card">
              <h3 className="text-sm font-medium text-warm-500 uppercase tracking-wide mb-4">
                Payment Settings
              </h3>

              {stripeStatus?.connected && stripeStatus.payouts_enabled ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-3 bg-success-50 rounded-lg">
                    <svg className="w-5 h-5 text-success-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-success-700 font-medium">Stripe Connected — You can receive payments</span>
                  </div>
                  <button
                    onClick={async () => {
                      const { url } = await getStripeDashboardUrl();
                      window.open(url, '_blank');
                    }}
                    className="w-full px-4 py-2 border border-warm-300 text-warm-700 rounded-lg hover:bg-warm-50 transition-colors"
                  >
                    Open Stripe Dashboard
                  </button>
                  <StripeDisconnectButton onDisconnected={loadData} />
                </div>
              ) : stripeStatus?.connected && stripeStatus.details_submitted && !stripeStatus.payouts_enabled ? (
                // Stripe onboarding completed, pending verification (1-3 business days)
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-4 bg-primary-50 border border-primary-200 rounded-lg">
                    <svg className="w-5 h-5 text-primary-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-primary-800 text-sm">
                      Stripe verification in progress. You'll be able to receive payments once Stripe completes their review (usually 1-3 business days).
                    </p>
                  </div>
                  <button
                    onClick={async () => {
                      const { url } = await getStripeDashboardUrl();
                      window.open(url, '_blank');
                    }}
                    className="w-full px-4 py-2 border border-warm-300 text-warm-700 rounded-lg hover:bg-warm-50 transition-colors"
                  >
                    Open Stripe Dashboard
                  </button>
                  <StripeDisconnectButton onDisconnected={loadData} label="Start fresh with a different account" />
                </div>
              ) : stripeStatus?.connected && !stripeStatus.payouts_enabled ? (
                // Has a Stripe account but onboarding not completed
                <div className="space-y-4">
                  <div className="p-4 bg-warning-50 border border-warning-200 rounded-lg">
                    <p className="text-warning-800 text-sm">
                      Your Stripe account is not fully set up. Complete your onboarding to receive payments.
                    </p>
                  </div>
                  <StripeSetupButton />
                  <StripeDisconnectButton onDisconnected={loadData} label="Start fresh with a different account" />
                </div>
              ) : (
                // No Stripe account connected
                <div className="space-y-4">
                  <div className="p-4 bg-primary-50 border border-primary-200 rounded-lg">
                    <p className="text-primary-800 text-sm">
                      No Stripe account connected. Set up your account to receive payments for teaching classes.
                    </p>
                  </div>
                  <StripeSetupButton label="Connect Stripe Account" />
                </div>
              )}

              <div className="mt-4 pt-4 border-t border-warm-200">
                <p className="text-sm text-warm-500">
                  Tax documents (W-9) are collected securely through Stripe during account setup.
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </FacultyLayout>
  );
}

/**
 * ProfileEditor - Edit profile form
 */
interface ProfileEditorProps {
  profile: FacultyProfile;
  onSave: () => void;
  onCancel: () => void;
}

function ProfileEditor({ profile, onSave, onCancel }: ProfileEditorProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    first_name: profile.first_name,
    last_name: profile.last_name,
    phone: profile.phone || '',
    bio: profile.bio || '',
  });
  const [headshot, setHeadshot] = useState<string | null>(profile.headshot_url);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleHeadshotChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be under 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => setHeadshot(e.target?.result as string);
    reader.readAsDataURL(file);

    try {
      setIsUploading(true);
      setError(null);
      const result = await uploadHeadshot(file);
      setHeadshot(result.headshot_url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.bio && formData.bio.length < 100) {
      setError('Bio must be at least 100 characters');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);
      await updateFacultyProfile({
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone: formData.phone || null,
        bio: formData.bio || null,
      });
      onSave();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="card">
      {/* Headshot Upload */}
      <div className="flex flex-col items-center sm:flex-row gap-4 sm:gap-6 mb-6">
        <div className="w-24 h-24 rounded-full bg-warm-100 overflow-hidden flex-shrink-0">
          {headshot ? (
            <img src={headshot} alt="Preview" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <svg className="w-10 h-10 text-warm-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
          )}
        </div>
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleHeadshotChange}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="px-4 py-2 border border-warm-300 text-warm-700 rounded-lg hover:bg-warm-50 transition-colors"
          >
            {isUploading ? 'Uploading...' : 'Change Photo'}
          </button>
        </div>
      </div>

      {/* Name Fields */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-warm-700 mb-1">
            First Name
          </label>
          <input
            type="text"
            value={formData.first_name}
            onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
            required
            className="w-full px-3 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-warm-700 mb-1">
            Last Name
          </label>
          <input
            type="text"
            value={formData.last_name}
            onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
            required
            className="w-full px-3 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Phone */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-warm-700 mb-1">
          Phone Number
        </label>
        <input
          type="tel"
          value={formData.phone}
          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          placeholder="(555) 555-5555"
          className="w-full px-3 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
      </div>

      {/* Bio */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-warm-700 mb-1">
          Bio
        </label>
        <textarea
          value={formData.bio}
          onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
          rows={6}
          placeholder="Enter bio here..."
          className="w-full px-3 py-2 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
        />
        <p className="text-sm text-warm-500 mt-1">
          {formData.bio.length}/2000 characters (min 100)
        </p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-danger-50 border border-danger-200 rounded-lg text-danger-700 text-sm">
          {error}
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-warm-300 text-warm-700 rounded-lg hover:bg-warm-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSaving}
          className="btn-primary"
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </form>
  );
}

/**
 * StripeSetupButton - Button to continue/reconnect Stripe setup
 */
interface StripeSetupButtonProps {
  label?: string;
}

function StripeSetupButton({ label = 'Continue Stripe Setup' }: StripeSetupButtonProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleStripeSetup = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await getStripeOnboardingUrl();

      if (!result?.url) {
        throw new Error('No onboarding URL returned from server');
      }

      window.location.href = result.url;
    } catch (err) {
      console.error('[StripeSetupButton] Error:', err);
      setError(err instanceof Error ? err.message : 'Failed to start Stripe setup');
      setIsLoading(false);
    }
  };

  return (
    <div>
      <button
        onClick={handleStripeSetup}
        disabled={isLoading}
        className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
      >
        {isLoading ? 'Redirecting to Stripe...' : label}
      </button>
      {error && (
        <p className="mt-2 text-sm text-danger-600">{error}</p>
      )}
    </div>
  );
}

/**
 * StripeDisconnectButton - Button to disconnect current Stripe account and connect a new one
 */
interface StripeDisconnectButtonProps {
  onDisconnected: () => void;
  label?: string;
}

function StripeDisconnectButton({ onDisconnected, label = "Connect a different Stripe account" }: StripeDisconnectButtonProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDisconnect = async () => {
    try {
      setIsLoading(true);
      setError(null);
      await disconnectStripeAccount();
      setShowConfirm(false);
      onDisconnected();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to disconnect Stripe');
      setIsLoading(false);
    }
  };

  if (showConfirm) {
    return (
      <div className="p-4 bg-warning-50 border border-warning-200 rounded-lg">
        <p className="text-warning-800 text-sm mb-3">
          Are you sure you want to disconnect your Stripe account? You'll need to complete Stripe onboarding again to receive payments.
        </p>
        <div className="flex gap-2">
          <button
            onClick={() => setShowConfirm(false)}
            disabled={isLoading}
            className="flex-1 px-3 py-2 border border-warm-300 text-warm-700 rounded-lg hover:bg-warm-50 transition-colors text-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleDisconnect}
            disabled={isLoading}
            className="flex-1 px-3 py-2 bg-danger-600 text-white rounded-lg hover:bg-danger-700 disabled:opacity-50 transition-colors text-sm"
          >
            {isLoading ? 'Disconnecting...' : 'Yes, Disconnect'}
          </button>
        </div>
        {error && (
          <p className="mt-2 text-sm text-danger-600">{error}</p>
        )}
      </div>
    );
  }

  return (
    <button
      onClick={() => setShowConfirm(true)}
      className="w-full px-4 py-2 text-warm-500 text-sm hover:text-warm-700 transition-colors"
    >
      {label}
    </button>
  );
}
